import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, CalendarDays, Database, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { SidebarContent, NAV_ITEMS } from "@/components/layout/Sidebar";
import { UserIdentity } from "@/components/layout/UserIdentity";
import { useDashboard } from "@/lib/dataContext";
import { useAuth } from "@/auth/AuthProvider";

export const Header = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { data, stateMeta } = useDashboard();
    const { logout } = useAuth();

    const current =
        NAV_ITEMS.find((n) => n.to === location.pathname)?.label || "Overview";
    const period = data?.reporting_period?.period_label || "April 2025 – March 2026";
    const stateName = data?.state_profile?.state_name || data?.executive_summary?.state_name || stateMeta?.state_name || "State";

    return (
        <header className="sticky top-0 z-20 border-b border-border/70 bg-background/85 backdrop-blur-md">
            <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-3">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                aria-label="Open navigation"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-[280px] border-sidebar-border p-0">
                            <SheetTitle className="sr-only">Dashboard navigation</SheetTitle>
                            <SheetDescription className="sr-only">Links to all dashboard sections</SheetDescription>
                            <SidebarContent onNavigate={() => setOpen(false)} />
                        </SheetContent>
                    </Sheet>
                    <div>
                        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            {String(stateName).trim()} State Dashboard
                        </p>
                        <h2 className="font-serif-elegant text-lg leading-tight text-foreground">
                            {current}
                        </h2>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <span className="hidden items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground sm:inline-flex">
                        <CalendarDays className="h-3.5 w-3.5 text-primary" />
                        {period}
                    </span>
                    <span className="hidden items-center gap-2 rounded-full border border-success/30 bg-success/10 px-3 py-1.5 text-xs font-medium text-success md:inline-flex">
                        <Database className="h-3.5 w-3.5" />
                        Approved data
                    </span>
                    <UserIdentity />
                    <Button variant="ghost" size="icon" onClick={logout} aria-label="Log out" title="Log out">
                        <LogOut className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </header>
    );
};
